import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { reportService, normalizeBranchScope, REPORT_TIME_DEFAULTS } from '../../services/reportService';
import { useAppStore } from '../../store/appStore';
import ReportActionBar from '../../components/reports/ReportActionBar';

const formatDate = (date) => {
  const d = new Date(date);
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const CaptainWiseSummary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();
  const params = new URLSearchParams(location.search);
  const startDateParam = params.get('from_date') || params.get('start_date');
  const endDateParam = params.get('to_date') || params.get('end_date');
  const requestedBranchId = params.get('branch_id');
  const branchId = normalizeBranchScope(requestedBranchId || selectedBranch || 'all');
  const fromTimeParam = params.get('from_time') || '';
  const toTimeParam = params.get('to_time') || '';

  const today = formatDate(new Date());
  const initialStartDate = startDateParam || today;
  const initialEndDate = endDateParam || today;
  const initialFromTime = fromTimeParam || REPORT_TIME_DEFAULTS.fromTime;
  const initialToTime = toTimeParam || REPORT_TIME_DEFAULTS.toTime;

  const [draftStartDate, setDraftStartDate] = useState(initialStartDate);
  const [draftEndDate, setDraftEndDate] = useState(initialEndDate);
  const [draftFromTime, setDraftFromTime] = useState(initialFromTime);
  const [draftToTime, setDraftToTime] = useState(initialToTime);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: initialStartDate,
    endDate: initialEndDate,
    fromTime: initialFromTime,
    toTime: initialToTime,
  });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await reportService.getBranchCaptainWiseSummary(
          appliedFilters.startDate,
          appliedFilters.endDate,
          branchId,
          appliedFilters.fromTime,
          appliedFilters.toTime,
        );
        const payload = response?.data?.data || {};
        const rows = Array.isArray(payload) ? payload : (payload.captain_wise_summary || []);
        setData(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setError(err?.response?.data?.message || err?.message || 'Network error');
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appliedFilters, branchId]);

  const handleApply = () => {
    if ((draftFromTime || draftToTime) && (!draftFromTime || !draftToTime)) {
      setError('Both from_time and to_time are required when using time filtering.');
      return false;
    }

    if (draftFromTime && draftToTime && draftFromTime > draftToTime) {
      setError('from_time must be less than or equal to to_time.');
      return false;
    }

    setError('');
    setAppliedFilters({
      startDate: draftStartDate,
      endDate: draftEndDate,
      fromTime: draftFromTime,
      toTime: draftToTime,
    });

    return true;
  };

  const handleReset = () => {
    const defaults = {
      startDate: today,
      endDate: today,
      fromTime: REPORT_TIME_DEFAULTS.fromTime,
      toTime: REPORT_TIME_DEFAULTS.toTime,
    };

    setDraftStartDate(defaults.startDate);
    setDraftEndDate(defaults.endDate);
    setDraftFromTime(defaults.fromTime);
    setDraftToTime(defaults.toTime);
    setAppliedFilters(defaults);
  };

  const totalSales = useMemo(
    () => data.reduce((sum, row) => sum + Number(row.total_sales || 0), 0),
    [data],
  );
  const totalOrders = useMemo(
    () => data.reduce((sum, row) => sum + Number(row.total_orders || 0), 0),
    [data],
  );
  const totalSuccessOrders = useMemo(
    () => data.reduce((sum, row) => sum + Number(row.success_orders || 0), 0),
    [data],
  );
  const currentBranchLabel = branchId === 'all' ? 'All Outlets' : (selectedBranch?.name || 'Selected Outlet');
  const reportColumns = [
    { label: 'Captain', key: 'captain_name' },
    { label: 'Total Orders', key: 'total_orders', type: 'number' },
    { label: 'Success Orders', key: 'success_orders', type: 'number' },
    { label: 'Total Sales', key: 'total_sales', type: 'currency' },
    { label: 'Paid Orders', key: 'paid_orders', type: 'number' },
  ];
  const reportSummary = [
    { label: 'Total Orders', value: totalOrders.toLocaleString('en-IN') },
    { label: 'Success Orders', value: totalSuccessOrders.toLocaleString('en-IN') },
    { label: 'Total Sales', value: `₹${Number(totalSales).toLocaleString('en-IN')}` },
  ];

  const formatTimeDisplay = (fromTime, toTime) => {
    if (!fromTime && !toTime) return 'Time Wise';
    return `${fromTime || REPORT_TIME_DEFAULTS.fromTime} - ${toTime || REPORT_TIME_DEFAULTS.toTime}`;
  };

  const filterSummary = useMemo(() => {
    if (!appliedFilters.startDate && !appliedFilters.endDate) return 'Report Period';
    const fromText = appliedFilters.startDate ? `${appliedFilters.startDate}` : 'N/A';
    const toText = appliedFilters.endDate ? `${appliedFilters.endDate}` : 'N/A';
    const fromTime = appliedFilters.fromTime || REPORT_TIME_DEFAULTS.fromTime;
    const toTime = appliedFilters.toTime || REPORT_TIME_DEFAULTS.toTime;
    return `Report Period: ${fromText} ${fromTime} → ${toText} ${toTime}`;
  }, [appliedFilters]);

  const summaryFooter = useMemo(() => (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,1))', boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)' }}>
        <Box component="tbody">
          <Box component="tr" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.06)' }}>
            <Box component="td" sx={{ p: 1.75, fontWeight: 800, width: '30%' }}>Grand Total</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '25%' }}>Orders: {totalOrders}</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '25%' }}>Success: {totalSuccessOrders}</Box>
            <Box component="td" sx={{ p: 1.75, fontWeight: 700, textAlign: 'right', width: '20%' }}>Sales: ₹{Number(totalSales).toLocaleString()}</Box>
          </Box>
        </Box>
      </Box>
    </Box>
  ), [totalOrders, totalSuccessOrders, totalSales]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,1))',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Stack spacing={2.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: 'text.primary' }}>Captain Wise Summary</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Performance by captain</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.25, ml: 'auto' }}>
              <ReportActionBar
                title="Captain Wise Summary"
                data={data}
                columns={reportColumns}
                summary={reportSummary}
                startDate={appliedFilters.startDate}
                endDate={appliedFilters.endDate}
                fromTime={appliedFilters.fromTime || REPORT_TIME_DEFAULTS.fromTime}
                toTime={appliedFilters.toTime || REPORT_TIME_DEFAULTS.toTime}
                branchLabel={currentBranchLabel}
                activeFilters={{}}
                filename="captain-wise-summary"
                onBack={() => navigate('/reports')}
              />
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 1,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'rgba(255,255,255,0.8)',
              width: { xs: '100%', md: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <TextField size="small" label="From Date" type="date" value={draftStartDate} onChange={(e) => setDraftStartDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} />
              <TextField size="small" label="To Date" type="date" value={draftEndDate} onChange={(e) => setDraftEndDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170, flex: { xs: '1 1 100%', sm: '0 0 auto' } }} />
              <Button variant="outlined" onClick={() => { setDraftFromTime(appliedFilters.fromTime || REPORT_TIME_DEFAULTS.fromTime); setDraftToTime(appliedFilters.toTime || REPORT_TIME_DEFAULTS.toTime); setTimeDialogOpen(true); }} startIcon={<CalendarTodayOutlinedIcon />} sx={{ minWidth: 180, textTransform: 'none', borderRadius: 2, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                {formatTimeDisplay(appliedFilters.fromTime, appliedFilters.toTime)}
              </Button>
              <Button variant="contained" onClick={handleApply} sx={{ borderRadius: 2, minWidth: 110 }}>Apply</Button>
              <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 2, minWidth: 110 }}>Reset</Button>
            </Box>
          </Paper>

          {filterSummary && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                label={filterSummary}
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2, px: 1, py: 0.5, fontWeight: 600 }}
              />
            </Box>
          )}

          {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

          {summaryFooter}

          {loading ? (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'auto', boxShadow: '0 8px 18px rgba(15,23,42,0.04)', background: '#fff' }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC' }}>Captain</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC' }}>Total Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC' }}>Success Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC' }}>Total Sales</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC' }}>Paid Orders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>Loading...</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : data.length === 0 ? (
            <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider', background: 'linear-gradient(180deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,1) 100%)' }}>
              <Box sx={{ fontSize: 40, mb: 1.5 }}>📄</Box>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>No Records Found</Typography>
              <Typography variant="body2" color="text.secondary">Try selecting a different date or time range.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'auto', boxShadow: '0 8px 18px rgba(15,23,42,0.04)', background: '#fff' }}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>Captain</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>Total Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>Success Orders</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>Total Sales</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, backgroundColor: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>Paid Orders</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow key={`${row.captain_name || 'captain'}-${index}`} hover sx={{ '&:nth-of-type(even)': { backgroundColor: 'rgba(148, 163, 184, 0.04)' }, '&:hover': { backgroundColor: 'rgba(37,99,235,0.04)' } }}>
                      <TableCell sx={{ py: 1.5 }}>{row.captain_name || 'Unassigned'}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>{row.total_orders || 0}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>{row.success_orders || 0}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>₹{Number(row.total_sales || 0).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>{row.paid_orders || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </Paper>

      <Dialog open={timeDialogOpen} onClose={() => setTimeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Time Wise</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="From Time" type="time" value={draftFromTime} onChange={(e) => setDraftFromTime(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
          <TextField label="To Time" type="time" value={draftToTime} onChange={(e) => setDraftToTime(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => { const ok = handleApply(); if (ok) setTimeDialogOpen(false); }}>Apply</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaptainWiseSummary;
